import { useCallback, useMemo } from "react";
import t from "../../../../i18n";
import {
  useCreateRemoteDashboardCallback,
  useDashboards,
} from "../../Dashboards/hook/api/dashboard";
import { useSummaryDashboards } from "../../../../api/useSummaryDashboards";
import { usePositionSummaryDashbardTabs } from "./usePositionTab";
import { usePost } from "../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import { toBasenamePath } from "../../../../router/helpers/navigation";
import { toPath } from "../../../../router/helpers/path";
import { useLockDashboardCallback } from "../../Dashboards/hook/action/lock";
import { useNavigate } from "react-router";
import type { DashboardBlockModelT } from "../../Dashboards/schema/dashboard";

export const useCreatePublishAndLinkDashboardCallback = ({ projectId }: { projectId: string }) => {
  const navigate = useNavigate();
  const summaryDashboards = useSummaryDashboards(projectId);
  const { dashboards: remoteDashboards } = useDashboards(projectId);
  const { summaryPos } = usePositionSummaryDashbardTabs(
    projectId,
    remoteDashboards,
    summaryDashboards,
  );
  const createRemoteDashboard = useCreateRemoteDashboardCallback();
  const createSummaryDashboard = usePost("/projects/:projectId(uuid)/summary/dashboards");
  const { addToast } = useToasts();

  const maxPosition = useMemo(
    () =>
      summaryDashboards.length > 0 ? Math.max(...summaryDashboards.map(sd => sd.position)) : 0,
    [summaryDashboards],
  );
  const getNextPosition = useCallback(
    () => (summaryPos >= maxPosition ? summaryPos + 1 : maxPosition + 1),
    [summaryPos, maxPosition],
  );

  const lockDashboard = useLockDashboardCallback(projectId);
  return useCallback(
    async (templateBlocks?: DashboardBlockModelT[]) => {
      try {
        const dashboard = {
          projectId,
          title: templateBlocks
            ? t("commons.title.plant_state")
            : t("project.dashboard.title.empty"),
          description: null,
          published: true,
          readonly: false,
        };

        const result = await createRemoteDashboard(
          dashboard,
          templateBlocks ? templateBlocks : undefined,
        );
        if (!result) throw new Error(t("errors.backend_error"));

        const dashboardId = result.dashboard.id;
        const newPosition = getNextPosition();

        await createSummaryDashboard({
          routeParams: { projectId },
          body: { dashboardId, position: newPosition },
        });
        await lockDashboard(result.dashboard.id);
        if (!templateBlocks)
          window.open(
            toBasenamePath(
              toPath("/projects/:projectId/dashboards/:dashboardId/edit", {
                projectId,
                dashboardId,
              }),
            ),
          );
        void navigate(
          toPath("/projects/:projectId/summary/dashboard/:dashboardId", {
            projectId,
            dashboardId,
          }),
        );
        return result;
      } catch (err) {
        if (err instanceof Error) addToast("error", err.message);
        else addToast("error", t("errors.backend_error"));
        return null;
      }
    },
    [
      projectId,
      createRemoteDashboard,
      getNextPosition,
      createSummaryDashboard,
      addToast,
      lockDashboard,
      navigate,
    ],
  );
};
